import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatCurrency as money } from '../../lib/format';
import { X, AlertTriangle, Plus, Trash2, Info } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { ProductPicker } from '../common/ProductPicker';
import { toast } from '../../lib/toast';

interface PreviewLine {
  productId: string;
  productName: string;
  breakdown: {
    basePrice: number;
    supervisorMarkup: number;
    distributorMarkup: number;
    finalPrice: number;
    houseKeep: number;
  };
  currentBestPrice: number | null;
  warnings: string[];
  errors: string[];
}

interface Row {
  productId: string;
  finalPrice: string;
  amCut: string;
  supervisorCut: string;
}

interface Props {
  accountId: string;
  accountName?: string;
  onClose: () => void;
  onCreated: () => void;
}

const emptyRow = (): Row => ({ productId: '', finalPrice: '', amCut: '', supervisorCut: '' });

/**
 * Create a negotiated deal for ONE clinic.
 *
 * The split is the whole point, so the form shows it live: for every row the
 * admin sees where each dollar lands (pharmacy cost / house / supervisor / AM)
 * and what the clinic pays today, rather than doing the subtraction themselves.
 */
export default function ClinicDealModal({ accountId, accountName, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<PreviewLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const previewSeq = useRef(0);

  // Rows that are numerically complete enough to price.
  const pricableRows = useMemo(
    () => rows.filter(r => r.productId && r.finalPrice !== '' && r.amCut !== ''),
    [rows]
  );

  const runPreview = useCallback(async () => {
    if (pricableRows.length === 0) {
      setPreview([]);
      return;
    }
    const seq = ++previewSeq.current;
    const res = await apiClient.previewClinicDeal({
      accountId,
      products: pricableRows.map(r => ({
        productId: r.productId,
        finalPrice: Number(r.finalPrice),
        amCut: Number(r.amCut),
        ...(r.supervisorCut !== '' ? { supervisorCut: Number(r.supervisorCut) } : {})
      }))
    });
    // Drop stale responses so fast typing can't leave older math on screen.
    if (seq !== previewSeq.current) return;
    if (res.success) setPreview((res as any).lines || res.data?.lines || []);
  }, [accountId, pricableRows]);

  useEffect(() => {
    const t = setTimeout(runPreview, 250);
    return () => clearTimeout(t);
  }, [runPreview]);

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const lineFor = (productId: string) => preview.find(p => p.productId === productId);

  const chosenIds = rows.map(r => r.productId).filter(Boolean);
  const duplicateProduct = new Set(chosenIds).size !== chosenIds.length;
  const anyErrors = preview.some(p => p.errors.length > 0);

  const valid =
    !!name.trim() &&
    pricableRows.length > 0 &&
    pricableRows.length === rows.filter(r => r.productId).length &&
    !duplicateProduct &&
    !anyErrors &&
    (!endDate || !startDate || endDate > startDate);

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    const res = await apiClient.createClinicDeal({
      accountId,
      name: name.trim(),
      products: pricableRows.map(r => ({
        productId: r.productId,
        finalPrice: Number(r.finalPrice),
        amCut: Number(r.amCut),
        ...(r.supervisorCut !== '' ? { supervisorCut: Number(r.supervisorCut) } : {})
      })),
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      note: note.trim() || undefined
    });
    setSubmitting(false);
    if (res.success) {
      toast.success('Deal created');
      onCreated();
      onClose();
    } else {
      toast.error(res.error || 'Failed to create deal');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">New deal</h2>
            {accountName && <p className="text-sm text-gray-500">{accountName}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Q3 semaglutide deal"
              maxLength={120}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              A deal <strong>overrides</strong> catalog pricing for this clinic — even when the catalog
              is cheaper. The cuts below are what the AM and supervisor are paid per unit; whatever
              is left above pharmacy cost is the house keep.
            </span>
          </div>

          {/* Product rows */}
          <div className="space-y-3">
            {rows.map((row, i) => {
              const line = lineFor(row.productId);
              const b = line?.breakdown;
              return (
                <div key={i} className="border border-gray-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <ProductPicker
                      value={row.productId}
                      onChange={p => setRow(i, { productId: p?._id || '' })}
                      excludeIds={rows.filter((_, idx) => idx !== i).map(r => r.productId).filter(Boolean)}
                    />
                    {rows.length > 1 && (
                      <button
                        onClick={() => setRows(rs => rs.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-red-600 p-2"
                        aria-label="Remove product"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {([
                      ['finalPrice', 'Clinic pays (z)'],
                      ['amCut', 'AM gets (x)'],
                      ['supervisorCut', 'Supervisor gets']
                    ] as const).map(([key, label]) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-600 mb-1">{label}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row[key]}
                          onChange={e => setRow(i, { [key]: e.target.value } as Partial<Row>)}
                          placeholder={key === 'supervisorCut' ? '0.00 (optional)' : '0.00'}
                          className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Live split — the reason this form exists */}
                  {b && (
                    <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 p-2.5">
                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        {[
                          ['Pharmacy cost', b.basePrice, 'text-gray-700'],
                          ['House keeps', b.houseKeep, b.houseKeep < 0 ? 'text-red-600' : 'text-emerald-700'],
                          ['Supervisor', b.supervisorMarkup, 'text-gray-700'],
                          ['AM', b.distributorMarkup, 'text-gray-700']
                        ].map(([label, value, cls]) => (
                          <div key={label as string}>
                            <div className="text-gray-500">{label as string}</div>
                            <div className={`font-semibold ${cls as string}`}>{money(value as number)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600 text-center">
                        Clinic pays <strong>{money(b.finalPrice)}</strong>
                        {line?.currentBestPrice !== null && line?.currentBestPrice !== undefined && (
                          <> · currently pays <strong>{money(line.currentBestPrice)}</strong></>
                        )}
                      </div>
                    </div>
                  )}

                  {line?.errors.map(err => (
                    <div key={err} className="mt-2 flex items-start gap-2 text-xs text-red-700">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{err}</span>
                    </div>
                  ))}
                  {line?.warnings.map(w => (
                    <div key={w} className="mt-2 flex items-start gap-2 text-xs text-amber-800">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              );
            })}

            <button
              onClick={() => setRows(rs => [...rs, emptyRow()])}
              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4" /> Add another product
            </button>

            {duplicateProduct && (
              <div className="flex items-start gap-2 text-xs text-red-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>The same product appears more than once. Each product needs exactly one price.</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starts</label>
              <input
                type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Defaults to now.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ends</label>
              <input
                type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Leave blank for open-ended.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea
              value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={500}
              placeholder="Why this deal exists — who negotiated it, what was promised."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!valid || submitting}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create deal'}
          </button>
        </div>
      </div>
    </div>
  );
}
